import * as React from "react";

import { Button, IButtonProps } from "@fluentui/react";
import { ITheme, mergeStyles } from "@fluentui/react/lib/Styling";
import { Async, KeyCodes } from "@fluentui/react/lib/Utilities";

import { dataUniqueId, InputMethod } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { iconNameToIconProps } from "@1js/acui-icons";
import { defaultKeytipExecute } from "@1js/acui-keytip";
import { AppThemeConsumer } from "@1js/acui-theme";
import { AppTooltipHost } from "@1js/acui-tooltip";

import { deriveButtonKeytip } from "../../utilities";
import { AppBaseButton } from "../AppBaseButton";
import { getLabelButtonProps } from "../AppBaseButton/getLabelButtonProps";
import {
  getAppCompoundButtonStyles,
  getTabularDescriptionClassNames,
} from "./AppCompoundButton.classNames";
import { AppCompoundButtonProps } from "./AppCompoundButton.Props";

/**
 * CompoundButtons are used to display buttons with a label and description. It looks something like below (Type 1)
 *
 *        **I**
 *        **C**   Label
 *        **O**   Description
 *        **N**
 *
 * However, some of our compound buttons can have tabular description as well like below (Type 2)
 *
 *        **I**
 *        **C**   Label
 *        **O**   A       B
 *        **N**   C       D
 *        *****
 *
 * If in the props, description is a string, we render of Type 1 compound buttons.
 * If the description prop is an array, we render Type 2 compound buttons.
 */
export class AppCompoundButton extends React.PureComponent<
  AppCompoundButtonProps,
  {}
> {
  private baseButton = React.createRef<AppBaseButton>();
  private navigationIdleTimeoutId: number | undefined;
  private isNavigationIdle: boolean;
  private readonly navigationIdleDelay_ms: number = 250;
  private async: Async;
  private tooltipHost: AppTooltipHost | undefined;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  constructor(props: AppCompoundButtonProps) {
    super(props);

    this.isNavigationIdle = true;
    this.async = new Async(this);
  }

  public componentDidMount() {
    this.initializeTooltipHost();
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
    }

    this.async.dispose();
  }

  public componentDidUpdate(prevProps: AppCompoundButtonProps) {
    if (
      this.props.customTooltip &&
      prevProps.customTooltip !== this.props.customTooltip
    ) {
      if (this.tooltipHost) {
        this.tooltipHost.updateTooltip(this.props.customTooltip);
      } else {
        this.initializeTooltipHost();
      }
    }
  }

  private initializeTooltipHost() {
    // The only time the button should have a tooltip is when there is a custom tooltip defined
    if (
      this.context.setTooltipElement &&
      this.context.updateTooltipsHidden &&
      this.props.customTooltip
    ) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        refElement: this.baseButton,
        controlId: this.props.id,
        controlLabel: this.props.customTooltip,
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: !!checkExperiment(
          this.context,
          ribbonExperiments.delayRenderTooltipsIsEnabled
        ),
      });
      this.tooltipHost.registerTooltipHostActions();
    }
  }

  public removeToolTip() {
    this.tooltipHost?.setTooltipUndefined();
  }

  public render() {
    let ariaLabel: string;
    if (this.props.ariaDescription && this.props.ariaDescription !== "") {
      ariaLabel =
        this.props.label + this.props.ariaDescription + this.props.description;
    }
    return (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <AppBaseButton
            ref={this.baseButton}
            styles={getAppCompoundButtonStyles(theme, this.props.styles)}
            onClick={this.onExecute}
            checked={this.props.toggled}
            toggle={this.props.toggleable}
            disabled={this.props.disabled}
            iconProps={iconNameToIconProps(
              this.props.icon,
              this.props.iconColor
            )}
            data={this.props.description}
            onRenderDescription={onRenderCompoundButtonDescription}
            {...getLabelButtonProps(this.props.label, undefined)}
            data-automation-type={
              this.props.dataAutomationType || "AppCompoundButton"
            }
            data-unique-id={dataUniqueId(this.props)}
            aria-setsize={this.props.setSize}
            aria-posinset={this.props.positionInSet}
            role={this.props.role}
            aria-checked={this.props.toggled}
            onMouseEnter={this.onMouseEnter}
            onMouseMove={this.onMouseMove}
            onMouseLeave={this.onMouseLeave}
            onWheel={this.onWheel}
            onKeyDown={this.onKeydown}
            keytipProps={deriveButtonKeytip(
              this.props.keytipProps,
              this.keytipExecute
            )}
            lang={this.props.lang}
            ariaLabel={ariaLabel}
            ariaDescription={
              this.props.ariaDescription || this.props.customTooltip
            }
            data-nav={this.props.dataNav}
          />
        )}
      </AppThemeConsumer>
    );
  }

  private keytipExecute = (executeTarget: HTMLElement) => {
    defaultKeytipExecute(executeTarget, this.executeFromKeytip);
  };

  private onExecute = (ev: React.MouseEvent<Button>) => {
    if (this.props.onExecute) {
      const { id, toggled, location, shouldTakeFocus } = this.props;
      this.props.onExecute({
        id,
        toggled,
        location,
        inputMethod: this.baseButton?.current?.getInputMethod(ev),
        shouldTakeFocus,
        triggerTime: ev && ev.timeStamp,
      });
    }
  };

  private executeFromKeytip = () => {
    if (this.props.onExecute) {
      const { id, toggled, location, shouldTakeFocus } = this.props;
      this.props.onExecute({
        id,
        toggled,
        location,
        inputMethod: InputMethod.Keyboard,
        shouldTakeFocus,
      });
    }
  };

  onMouseMove = (ev: React.MouseEvent<HTMLButtonElement>): void => {
    if (this.props.enableFocusOnHover) {
      const targetElement = ev.currentTarget as HTMLElement;

      // If navigation is not idle or the targetElement is the focused element bail out
      if (
        !this.isNavigationIdle ||
        (document && targetElement === (document.activeElement as HTMLElement))
      ) {
        return;
      }

      targetElement.focus();
    }
  };

  private onMouseEnter = (ev: React.MouseEvent<HTMLButtonElement>) => {
    if (this.props.enableFocusOnHover) {
      if (this.isNavigationIdle && !this.props.disabled) {
        ev.currentTarget.focus();
      }
    }
  };

  private onMouseLeave = (ev: React.MouseEvent<HTMLButtonElement>): void => {
    if (this.props.enableFocusOnHover) {
      if (this.isNavigationIdle && !this.props.disabled) {
        // Get the parent menu of the target and focus it
        const elements = document.querySelectorAll(
          '[tabindex="0"][role="menu"]'
        );

        for (let index = 0; index < elements.length; index += 1) {
          if (elements[index].contains(ev.currentTarget)) {
            /**
             * IE11 focus() method forces parents to scroll to top of element.
             * Edge and IE expose a setActive() function for focusable divs that
             * sets the page focus but does not scroll the parent element.
             */
            if ((elements[index] as any).setActive) {
              (elements[index] as any).setActive();
            } else {
              (elements[index] as HTMLElement).focus();
            }

            break;
          }
        }
      }
    }
  };

  private onWheel = () => {
    this.setNavigationTimeout();
  };

  private onKeydown = (ev: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (
      ev.which === KeyCodes.up ||
      ev.which === KeyCodes.down ||
      ev.which === KeyCodes.left ||
      ev.which === KeyCodes.right
    ) {
      this.setNavigationTimeout();
    }
  };

  /**
   * Sets a timeout so we won't process any mouse "hover" events
   * while navigating (via mouseWheel or arrowKeys)
   */
  private setNavigationTimeout = () => {
    if (!this.isNavigationIdle && this.navigationIdleTimeoutId !== undefined) {
      this.async.clearTimeout(this.navigationIdleTimeoutId);
      this.navigationIdleTimeoutId = undefined;
    } else {
      this.isNavigationIdle = false;
    }

    this.navigationIdleTimeoutId = this.async.setTimeout(() => {
      this.isNavigationIdle = true;
    }, this.navigationIdleDelay_ms);
  };
}

export function onRenderCompoundButtonDescription(props: IButtonProps) {
  let description = props.data;
  if (typeof description !== "string") {
    description = <AppCompoundButtonTabularDescription {...props} />;
  }

  return (
    <div className={props.styles && mergeStyles(props.styles.description)}>
      {description}
    </div>
  );
}

const AppCompoundButtonTabularDescription: React.StatelessComponent<
  IButtonProps
> = (props: IButtonProps) => {
  const classNames = getTabularDescriptionClassNames();
  const tableData: string[][] = props.data;

  return (
    <table className={classNames.table}>
      <tbody>
        {tableData.map((rows: string[], rowIndex) => (
          <tr key={`${props.id}-${rowIndex}-row`}>
            {rows.map((cell, cellIndex) => (
              <td
                key={`${props.id}-${rowIndex}${cellIndex}-column`}
                className={
                  cellIndex === 1
                    ? classNames.columnWithSeparation
                    : classNames.column
                }
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
